import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Save, LogOut, Upload, Trash2, Plus, ImagePlus, ExternalLink,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSite, API, resolveImageUrl } from "../context/SiteContext";

const uuid = () =>
  (crypto?.randomUUID && crypto.randomUUID()) ||
  "id-" + Math.random().toString(36).slice(2);

export default function AdminEditor() {
  const { user, checking, logout, authHeader } = useAuth();
  const { site, refresh, setSite } = useSite();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    if (site && !draft) setDraft(JSON.parse(JSON.stringify(site)));
  }, [site, draft]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <Loader2 className="w-6 h-6 animate-spin text-[#3A5A40]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin" replace />;
  if (!draft) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <Loader2 className="w-6 h-6 animate-spin text-[#3A5A40]" />
      </div>
    );
  }

  const update = (k) => (e) =>
    setDraft((d) => ({ ...d, [k]: e?.target ? e.target.value : e }));

  const updateTeamField = (idx, k, v) =>
    setDraft((d) => {
      const team = [...(d.team || [])];
      team[idx] = { ...team[idx], [k]: v };
      return { ...d, team };
    });

  const addMember = () =>
    setDraft((d) => ({
      ...d,
      team: [
        ...(d.team || []),
        { id: uuid(), name: "New therapist", title: "", spec: "", image_url: "" },
      ],
    }));

  const removeMember = (idx) =>
    setDraft((d) => {
      const team = [...(d.team || [])];
      team.splice(idx, 1);
      return { ...d, team };
    });

  const updateServiceField = (idx, k, v) =>
    setDraft((d) => {
      const services = [...(d.services || [])];
      services[idx] = { ...services[idx], [k]: v };
      return { ...d, services };
    });

  const addService = () =>
    setDraft((d) => ({
      ...d,
      services: [
        ...(d.services || []),
        { id: uuid(), name: "New service", blurb: "", icon: "Sparkles", tone: "surface" },
      ],
    }));

  const removeService = (idx) =>
    setDraft((d) => {
      const services = [...(d.services || [])];
      services.splice(idx, 1);
      return { ...d, services };
    });

  const handleUpload = async (file, applyUrl, key) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5 MB or smaller.");
      return;
    }
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads`, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      applyUrl(data.url);
      toast.success("Image uploaded.");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploadingKey(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/site`, draft, { headers: authHeader() });
      setSite(data);
      setDraft(JSON.parse(JSON.stringify(data)));
      toast.success("Changes saved & live on the public site.");
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F4F1EA] pb-32" data-testid="admin-editor">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#E2DCD0]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-serif text-xl text-[#1C1917]">Supervisor Dashboard</span>
            <span className="hidden sm:inline-block text-xs uppercase tracking-[0.2em] text-[#9F5C45] border border-[#9F5C45]/30 px-2 py-1">
              {user.email}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/dashboard"
              data-testid="admin-nav-dashboard"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 btn-ghost text-xs tracking-wide"
            >
              Dashboard
            </Link>
            <Link
              to="/"
              data-testid="admin-view-site"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 btn-ghost text-xs tracking-wide"
            >
              View site <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <button
              onClick={save}
              disabled={saving}
              data-testid="admin-save-button"
              className="inline-flex items-center gap-2 px-5 py-2.5 btn-primary text-xs tracking-wide disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 btn-ghost text-xs tracking-wide"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-10 pt-12 md:pt-16 space-y-16">
        {/* Brand */}
        <Section number="01" title="Brand" subtitle="Your clinic’s public name, tagline and optional logo image.">
          <Field label="Clinic name">
            <input className="lgw-field" data-testid="field-brand_name" value={draft.brand_name} onChange={update("brand_name")} />
          </Field>
          <Field label="Tagline / subtitle">
            <input className="lgw-field" data-testid="field-brand_tagline" value={draft.brand_tagline} onChange={update("brand_tagline")} />
          </Field>
          <ImageField
            label="Logo (optional — shown next to the brand name in the navbar)"
            url={draft.brand_logo_url}
            onUrlChange={(v) => update("brand_logo_url")(v)}
            onUpload={(file) => handleUpload(file, (u) => update("brand_logo_url")(u), "logo")}
            uploading={uploadingKey === "logo"}
            testid="brand-logo-image"
          />
        </Section>

        {/* Hero */}
        <Section number="02" title="Hero" subtitle="The first thing visitors read and see.">
          <Field label="Overline (small label)">
            <input className="lgw-field" data-testid="field-hero_overline" value={draft.hero_overline} onChange={update("hero_overline")} />
          </Field>
          <div className="grid md:grid-cols-3 gap-7">
            <Field label="Headline — first part">
              <input className="lgw-field" data-testid="field-hero_title_pre" value={draft.hero_title_pre} onChange={update("hero_title_pre")} />
            </Field>
            <Field label="Headline — italic accent">
              <input className="lgw-field" data-testid="field-hero_title_em" value={draft.hero_title_em} onChange={update("hero_title_em")} />
            </Field>
            <Field label="Headline — end part">
              <input className="lgw-field" data-testid="field-hero_title_post" value={draft.hero_title_post} onChange={update("hero_title_post")} />
            </Field>
          </div>
          <Field label="Subtitle paragraph">
            <textarea rows={4} className="lgw-field resize-none" data-testid="field-hero_subtitle" value={draft.hero_subtitle} onChange={update("hero_subtitle")} />
          </Field>
          <ImageField
            label="Hero background image"
            url={draft.hero_image_url}
            onUrlChange={(v) => update("hero_image_url")(v)}
            onUpload={(file) => handleUpload(file, (u) => update("hero_image_url")(u), "hero")}
            uploading={uploadingKey === "hero"}
            testid="hero-image"
          />
        </Section>

        {/* Contact */}
        <Section number="03" title="Contact & location" subtitle="Address, phone, email and hours that appear in the footer and Visit section.">
          <div className="grid md:grid-cols-2 gap-7">
            <Field label="Address line 1">
              <input className="lgw-field" data-testid="field-address_line1" value={draft.address_line1} onChange={update("address_line1")} />
            </Field>
            <Field label="Address line 2 (city, state, zip)">
              <input className="lgw-field" data-testid="field-address_line2" value={draft.address_line2} onChange={update("address_line2")} />
            </Field>
            <Field label="Phone number">
              <input className="lgw-field" data-testid="field-phone" value={draft.phone} onChange={update("phone")} />
            </Field>
            <Field label="Secondary phone (cell)">
              <input className="lgw-field" data-testid="field-phone2" value={draft.phone2 || ""} onChange={update("phone2")} placeholder="e.g. 203-768-6969" />
            </Field>
            <Field label="Email">
              <input className="lgw-field" type="email" data-testid="field-email" value={draft.email} onChange={update("email")} />
            </Field>
            <Field label="Weekday hours">
              <input className="lgw-field" data-testid="field-hours_weekday" value={draft.hours_weekday} onChange={update("hours_weekday")} />
            </Field>
            <Field label="Weekend hours">
              <input className="lgw-field" data-testid="field-hours_weekend" value={draft.hours_weekend} onChange={update("hours_weekend")} />
            </Field>
            <Field label="Google Maps search query">
              <input className="lgw-field" data-testid="field-map_query" value={draft.map_query} onChange={update("map_query")} />
            </Field>
          </div>
        </Section>

        {/* Booking */}
        <Section number="04" title="Online booking" subtitle="Paste your Calendly, Cal.com or Acuity embed URL — leave blank to hide the booking section on the public site.">
          <Field label="Booking embed URL">
            <input
              className="lgw-field"
              data-testid="field-booking_url"
              placeholder="https://calendly.com/your-account/intro-call"
              value={draft.booking_url || ""}
              onChange={update("booking_url")}
            />
          </Field>
          <p className="text-xs text-[#57534E]">
            Tip: Calendly &amp; Cal.com both work. Just copy the public link to your event type and paste it here.
          </p>
        </Section>

        {/* Background music */}
        <Section number="04b" title="Background music (optional)" subtitle="A soft floating player appears in the corner of every public page. Visitors must click once to start it (browsers block autoplay). Leave blank to hide.">
          <div className="grid md:grid-cols-[1fr_auto] gap-5">
            <Field label="Music URL (.mp3 or .ogg, direct link)">
              <input
                className="lgw-field"
                data-testid="field-background_music_url"
                placeholder="https://cdn.pixabay.com/audio/..../track.mp3"
                value={draft.background_music_url || ""}
                onChange={update("background_music_url")}
              />
            </Field>
            <Field label="Track label">
              <input
                className="lgw-field"
                data-testid="field-background_music_label"
                placeholder="Ambient music"
                value={draft.background_music_label || ""}
                onChange={update("background_music_label")}
              />
            </Field>
          </div>
          <div className="text-xs text-[#57534E] space-y-2">
            <p><strong className="text-[#1C1917]">Where to find free, commercial-safe music:</strong></p>
            <ul className="list-disc pl-5 space-y-1">
              <li><a href="https://pixabay.com/music/search/genre/ambient/" target="_blank" rel="noreferrer" className="text-[#9F5C45] underline">Pixabay Music · Ambient</a> — totally free, no attribution required. Right-click a track → &quot;Copy audio URL&quot; or download &amp; host on your own server.</li>
              <li><a href="https://mixkit.co/free-stock-music/tag/ambient/" target="_blank" rel="noreferrer" className="text-[#9F5C45] underline">Mixkit · Ambient</a> — free for commercial use.</li>
              <li><a href="https://www.epidemicsound.com/" target="_blank" rel="noreferrer" className="text-[#9F5C45] underline">Epidemic Sound</a> — paid, what most spas use.</li>
            </ul>
            <p>Paste a direct .mp3 link above. If your hosting blocks hot-linking, download the track and upload it to a Cloudinary / Drive / S3 link.</p>
          </div>
        </Section>

        {/* Services */}
        <Section number="05" title="Services we offer" subtitle="What appears in the bento grid on the public site. Add, edit and remove freely.">
          <div className="space-y-6">
            {(draft.services || []).map((s, idx) => (
              <article key={s.id || idx} data-testid={`service-row-${idx}`} className="bg-white border border-[#E2DCD0] p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-start">
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Service name">
                    <input className="lgw-field" data-testid={`service-${idx}-name`} value={s.name || ""} onChange={(e) => updateServiceField(idx, "name", e.target.value)} />
                  </Field>
                  <Field label="Lucide icon name (e.g. Brain, Leaf, Flower2)">
                    <input className="lgw-field" data-testid={`service-${idx}-icon`} value={s.icon || ""} onChange={(e) => updateServiceField(idx, "icon", e.target.value)} placeholder="Sparkles" />
                  </Field>
                  <Field label="Short description">
                    <textarea rows={2} className="lgw-field resize-none" data-testid={`service-${idx}-blurb`} value={s.blurb || ""} onChange={(e) => updateServiceField(idx, "blurb", e.target.value)} />
                  </Field>
                  <Field label="Color tone">
                    <select className="lgw-field" data-testid={`service-${idx}-tone`} value={s.tone || "surface"} onChange={(e) => updateServiceField(idx, "tone", e.target.value)}>
                      <option value="dark">Deep sage (dark)</option>
                      <option value="accent">Terracotta (accent)</option>
                      <option value="secondary">Stone (secondary)</option>
                      <option value="surface">Bone (surface)</option>
                      <option value="neutral">Outline (neutral)</option>
                    </select>
                  </Field>
                </div>
                <button
                  onClick={() => removeService(idx)}
                  data-testid={`service-${idx}-remove`}
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs text-[#9F5C45] hover:bg-[#9F5C45]/10 transition-colors border border-[#9F5C45]/30"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              </article>
            ))}
            <button
              onClick={addService}
              data-testid="service-add"
              className="w-full inline-flex items-center justify-center gap-2 py-6 border border-dashed border-[#9F5C45]/40 text-[#9F5C45] hover:bg-[#9F5C45]/5 transition-colors text-sm tracking-wide"
            >
              <Plus className="w-4 h-4" /> Add service
            </button>
          </div>
        </Section>

        {/* Team */}
        <Section number="06" title="Our therapists" subtitle="Add, edit and remove team members. They appear in the order listed below.">
          <div className="space-y-6">
            {(draft.team || []).map((m, idx) => (
              <article key={m.id || idx} data-testid={`team-row-${idx}`} className="bg-white border border-[#E2DCD0] p-6 md:p-8 grid md:grid-cols-[180px_1fr_auto] gap-6 items-start">
                <div className="space-y-3">
                  <div className="aspect-[3/4] bg-[#F4F1EA] overflow-hidden border border-[#E2DCD0]">
                    {m.image_url && (
                      <img src={resolveImageUrl(m.image_url)} alt={m.name} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <UploadButton
                    label="Replace photo"
                    uploading={uploadingKey === `team-${idx}`}
                    onFile={(f) => handleUpload(f, (u) => updateTeamField(idx, "image_url", u), `team-${idx}`)}
                    testid={`team-${idx}-upload`}
                  />
                </div>
                <div className="space-y-5">
                  <Field label="Name">
                    <input className="lgw-field" data-testid={`team-${idx}-name`} value={m.name || ""} onChange={(e) => updateTeamField(idx, "name", e.target.value)} />
                  </Field>
                  <Field label="Title">
                    <input className="lgw-field" data-testid={`team-${idx}-title`} value={m.title || ""} onChange={(e) => updateTeamField(idx, "title", e.target.value)} />
                  </Field>
                  <Field label="Specialty">
                    <input className="lgw-field" data-testid={`team-${idx}-spec`} value={m.spec || ""} onChange={(e) => updateTeamField(idx, "spec", e.target.value)} />
                  </Field>
                  <Field label="Bio (shown on hover / in the modal)">
                    <textarea
                      rows={4}
                      className="lgw-field resize-none"
                      data-testid={`team-${idx}-bio`}
                      placeholder="e.g. Dr. Olsen brings 15 years of trauma-informed practice. Her warmth makes the deepest work feel possible."
                      value={m.bio || ""}
                      onChange={(e) => updateTeamField(idx, "bio", e.target.value)}
                    />
                  </Field>
                  <Field label="Image URL (or use Replace photo)">
                    <input className="lgw-field" data-testid={`team-${idx}-image_url`} value={m.image_url || ""} onChange={(e) => updateTeamField(idx, "image_url", e.target.value)} placeholder="https://… or /api/uploads/…" />
                  </Field>
                </div>
                <button
                  onClick={() => removeMember(idx)}
                  data-testid={`team-${idx}-remove`}
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs text-[#9F5C45] hover:bg-[#9F5C45]/10 transition-colors border border-[#9F5C45]/30"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              </article>
            ))}
            <button
              onClick={addMember}
              data-testid="team-add"
              className="w-full inline-flex items-center justify-center gap-2 py-6 border border-dashed border-[#9F5C45]/40 text-[#9F5C45] hover:bg-[#9F5C45]/5 transition-colors text-sm tracking-wide"
            >
              <Plus className="w-4 h-4" /> Add therapist
            </button>
          </div>
        </Section>

        <div className="flex justify-end pt-4">
          <button
            onClick={save}
            disabled={saving}
            data-testid="admin-save-bottom"
            className="inline-flex items-center gap-2 px-8 py-4 btn-primary text-sm tracking-wide disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save all changes
          </button>
        </div>
      </div>
    </main>
  );
}

// ---------- Subcomponents ----------
const Section = ({ number, title, subtitle, children }) => (
  <section className="space-y-7">
    <div className="border-b border-[#E2DCD0] pb-6">
      <p className="overline text-[#9F5C45] mb-3">{number} — Section</p>
      <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] tracking-tight">{title}</h2>
      {subtitle && <p className="text-[#57534E] mt-2 max-w-2xl leading-relaxed">{subtitle}</p>}
    </div>
    <div className="space-y-7">{children}</div>
  </section>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="overline text-[#57534E] mb-2 block">{label}</span>
    {children}
  </label>
);

const UploadButton = ({ label, onFile, uploading, testid }) => {
  const ref = useRef(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => onFile(e.target.files?.[0])}
      />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={uploading}
        data-testid={testid}
        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 btn-ghost text-xs tracking-wide disabled:opacity-60"
      >
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {label}
      </button>
    </>
  );
};

const ImageField = ({ label, url, onUrlChange, onUpload, uploading, testid }) => {
  const ref = useRef(null);
  return (
    <div className="space-y-3">
      <span className="overline text-[#57534E] block">{label}</span>
      <div className="grid md:grid-cols-[260px_1fr] gap-6 items-start">
        <div className="aspect-[16/10] bg-[#F4F1EA] overflow-hidden border border-[#E2DCD0]">
          {url && (
            <img src={resolveImageUrl(url)} alt="" className="w-full h-full object-cover" />
          )}
        </div>
        <div className="space-y-3">
          <input
            ref={ref}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`${testid}-file-input`}
            onChange={(e) => onUpload(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => ref.current?.click()}
            disabled={uploading}
            data-testid={`${testid}-upload-btn`}
            className="inline-flex items-center gap-2 px-5 py-3 btn-primary text-xs tracking-wide disabled:opacity-60"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            Upload from computer
          </button>
          <input
            className="lgw-field"
            data-testid={`${testid}-url-input`}
            placeholder="Or paste an image URL"
            value={url || ""}
            onChange={(e) => onUrlChange(e.target.value)}
          />
          <p className="text-xs text-[#57534E]">PNG, JPG or WebP. Max 5 MB. Landscape orientation works best.</p>
        </div>
      </div>
    </div>
  );
};
